import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Sparkles, Copy, Check, RefreshCw, Zap, Lock, FileCheck, Download } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function CoverLetterModal({ open, onOpenChange, application }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.tier === "pro";

  const [background, setBackground] = useState("");
  const [loadedProfile, setLoadedProfile] = useState(false);
  const [letter, setLetter] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savedToLocker, setSavedToLocker] = useState(false);
  const [savedDocId, setSavedDocId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLetter("");
    setCopied(false);
    setSavedToLocker(false);
    setSavedDocId(null);
    if (!isPro) return;
    (async () => {
      try {
        const { data } = await api.get("/profile");
        setBackground(data.background || "");
      } catch { /* ignore */ }
      setLoadedProfile(true);
    })();
  }, [open, isPro]);

  const generate = async () => {
    if (!isPro) {
      onOpenChange(false);
      navigate("/upgrade");
      return;
    }
    if (!application?.id) return;
    if (background.trim().length < 20) {
      toast.error("Add a short background about yourself first (a couple of sentences is plenty).");
      return;
    }
    setLoading(true);
    setLetter("");
    setSavedToLocker(false);
    try {
      const { data } = await api.post("/ai/cover-letter", {
        application_id: application.id,
        background,
      });
      setLetter(data.text);
      toast.success("Cover letter ready");
      // Auto-save the generated letter as a PDF into the Documents Locker so the
      // user keeps the exact version they sent. Non-blocking: a save failure
      // does not disrupt the UX — they can still copy the letter.
      try {
        const { data: saved } = await api.post("/ai/cover-letter/save-pdf", {
          application_id: application.id,
          text: data.text,
        });
        setSavedToLocker(true);
        setSavedDocId(saved?.id || null);
        toast.success("Saved to Documents");
      } catch (saveErr) {
        // Silently log — the primary generation flow succeeded.
        console.warn("Cover letter PDF auto-save failed:", saveErr);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSavedDoc = async () => {
    if (!savedDocId) return;
    try {
      const res = await api.get(`/documents/${savedDocId}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Open in a new tab so users can preview or forward the PDF.
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const handleDownloadPdf = async () => {
    if (!savedDocId) {
      toast.error("Still saving — try again in a moment.");
      return;
    }
    try {
      const res = await api.get(`/documents/${savedDocId}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const company = (application?.company || "role").trim();
      a.download = `Cover letter — ${company}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(letter);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#FDFCFA] max-h-[90vh] overflow-y-auto" data-testid="cover-letter-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A362D] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D99C3D]" />
            Cover letter — {application?.company}
          </DialogTitle>
          <DialogDescription>
            Trackr will tailor a warm-professional letter to {application?.job_title} at {application?.company}.
          </DialogDescription>
        </DialogHeader>

        {!isPro && (
          <div className="rounded-xl bg-[#1A362D] text-[#F4F1EE] p-4 flex items-start gap-3" data-testid="cover-letter-pro-gate">
            <Lock className="w-4 h-4 text-[#D99C3D] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">AI cover letters are a Trackr Pro feature.</p>
              <p className="text-sm opacity-80 mt-0.5">Upgrade for unlimited tailored drafts.</p>
            </div>
          </div>
        )}

        <div className="mt-2">
          <label className="text-sm font-medium text-[#1A362D]">Your background</label>
          <Textarea
            rows={4}
            value={background}
            onChange={(e) => setBackground(e.target.value)}
            placeholder="A few sentences about your experience, strengths, and what you're looking for. Trackr saves this for future letters."
            className="mt-1.5 bg-white"
            disabled={!isPro || loading}
            data-testid="cover-letter-background"
          />
        </div>

        {letter ? (
          <div className="mt-4 rounded-xl bg-white border border-[#E5E1DB] p-5" data-testid="cover-letter-output">
            {savedToLocker && (
              <div
                className="mb-3 inline-flex items-center gap-2 text-xs bg-[#E2F0E7] text-[#1A362D] rounded-full px-3 py-1"
                data-testid="cover-letter-saved-badge"
              >
                <FileCheck className="w-3.5 h-3.5" />
                <span>Saved to Documents · PDF</span>
                {savedDocId && (
                  <>
                    <span className="opacity-40">·</span>
                    <button
                      onClick={handleViewSavedDoc}
                      className="underline hover:text-[#C65D47] font-medium"
                      data-testid="cover-letter-view-saved"
                    >
                      View in Documents
                    </button>
                  </>
                )}
              </div>
            )}
            <pre className="whitespace-pre-wrap font-body text-[15px] leading-relaxed text-[#1A362D]">{letter}</pre>
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-dashed border-[#E5E1DB] p-8 text-center text-[#5C6B64]">
            Click generate to draft your letter.
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="cover-letter-close">
            Close
          </Button>
          {letter && (
            <Button
              variant="outline"
              onClick={copy}
              className="rounded-full"
              data-testid="cover-letter-copy"
            >
              {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy</>}
            </Button>
          )}
          {letter && savedDocId && (
            <Button
              variant="outline"
              onClick={handleDownloadPdf}
              className="rounded-full"
              data-testid="cover-letter-download"
            >
              <Download className="w-4 h-4 mr-1.5" /> Download PDF
            </Button>
          )}
          <Button
            onClick={generate}
            disabled={loading || !loadedProfile && isPro}
            className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white px-6"
            data-testid="cover-letter-generate"
          >
            {loading ? "Writing…" : letter ? (<><RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate</>) : isPro ? (<><Sparkles className="w-4 h-4 mr-1.5" /> Generate</>) : (<><Zap className="w-4 h-4 mr-1.5" /> Upgrade to use</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
